import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Shield, ShieldCheck, Copy, Loader2, KeyRound, Smartphone, Check,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * /security/2fa — Set up TOTP with an authenticator app (Google Authenticator,
 * Authy, 1Password). Also lets the user view/regenerate their 10 backup codes
 * and disable 2FA (requires a code).
 */
const TwoFactorSettingsPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    const res = await api.get('/me/2fa/status');
    setStatus(res.data);
  };
  useEffect(() => { loadStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startSetup = async () => {
    setBusy(true);
    try {
      const res = await api.post('/me/2fa/setup');
      setSetupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async () => {
    if (!verifyCode.trim()) return;
    setBusy(true);
    try {
      const res = await api.post('/me/2fa/verify', { code: verifyCode });
      setBackupCodes(res.data.backup_codes);
      setSetupData(null);
      setVerifyCode('');
      toast.success('2FA enabled! Save your backup codes somewhere safe.');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const doDisable = async () => {
    if (!disableCode.trim()) return;
    if (!window.confirm('Disable two-factor authentication? Your account will only require a password.')) return;
    setBusy(true);
    try {
      await api.post('/me/2fa/disable', { code: disableCode });
      setDisableCode('');
      setBackupCodes(null);
      toast.success('2FA disabled');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const regenerateCodes = async () => {
    const code = window.prompt('Enter a fresh code from your authenticator to regenerate:');
    if (!code) return;
    setBusy(true);
    try {
      const res = await api.post('/me/2fa/backup-codes/regenerate', { code });
      setBackupCodes(res.data.backup_codes);
      toast.success('New backup codes generated. The old ones no longer work.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Codes copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="2fa-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <Link to="/profile" className="inline-block hover:opacity-70" data-testid="2fa-title-link">
              <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                <Shield className="w-6 h-6 text-indigo-600" /> Two-Factor Authentication
              </h1>
            </Link>
            <p className="text-sm text-slate-500">Add an authenticator app for extra security on every login.</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 md:p-6 space-y-4">
        {status === null && (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        )}

        {/* Enabled state */}
        {status?.enabled && !setupData && (
          <Card className="border-2 border-emerald-200 bg-emerald-50">
            <CardContent className="p-5 flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-emerald-600" />
              <div className="flex-1">
                <p className="font-bold text-emerald-900">2FA is enabled</p>
                <p className="text-xs text-emerald-700">
                  {status.backup_codes_remaining} backup code{status.backup_codes_remaining === 1 ? '' : 's'} remaining.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Disabled — enable flow */}
        {status && !status.enabled && !setupData && (
          <Card>
            <CardContent className="p-5 space-y-3">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-indigo-600" /> Set up an authenticator app
              </h2>
              <p className="text-sm text-slate-600">
                We'll show a QR code you can scan with Google Authenticator, Authy, or 1Password.
                After you scan and enter one code to prove it works, we'll turn on 2FA and give you 10 backup codes.
              </p>
              <Button onClick={startSetup} disabled={busy} data-testid="2fa-start-setup">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
                Begin setup
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Setup dialog */}
        {setupData && (
          <Card>
            <CardContent className="p-5 space-y-4">
              <h2 className="font-bold text-slate-800">Step 1: Scan this QR code</h2>
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <img src={setupData.qr_code_data_url} alt="TOTP QR" className="w-48 h-48 border rounded-lg" data-testid="2fa-qr" />
                <div className="text-sm text-slate-600 flex-1">
                  <p>Can&apos;t scan? Enter this secret manually in your app:</p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 bg-slate-100 px-3 py-2 rounded font-mono text-xs break-all" data-testid="2fa-secret">
                      {setupData.secret}
                    </code>
                    <Button variant="outline" size="sm" onClick={() => {navigator.clipboard.writeText(setupData.secret); toast.success('Copied');}}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
              <h2 className="font-bold text-slate-800 pt-2">Step 2: Enter a 6-digit code from the app</h2>
              <div className="flex gap-2">
                <Input
                  placeholder="123456"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  className="max-w-[160px] font-mono text-lg text-center tracking-widest"
                  maxLength={6}
                  data-testid="2fa-verify-input"
                />
                <Button onClick={doVerify} disabled={busy || verifyCode.length < 6} data-testid="2fa-verify-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Verify & enable
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Backup codes card */}
        {backupCodes && (
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-bold text-amber-900 flex items-center gap-2">
                  <KeyRound className="w-5 h-5" /> Backup codes — save these NOW
                </h2>
                <Button variant="outline" size="sm" onClick={copyCodes} data-testid="2fa-copy-codes">
                  <Copy className="w-4 h-4 mr-1" /> Copy all
                </Button>
              </div>
              <p className="text-xs text-amber-800">
                Each code works ONCE. If you lose your phone you can use one to log in. Store them like passwords.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-sm" data-testid="2fa-backup-codes">
                {backupCodes.map((c) => (
                  <div key={c} className="bg-white border border-amber-300 rounded px-2 py-1 text-center">{c}</div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Enabled: manage codes + disable */}
        {status?.enabled && !setupData && (
          <>
            <Card>
              <CardContent className="p-5 space-y-3">
                <h2 className="font-bold text-slate-800">Backup codes</h2>
                <p className="text-sm text-slate-600">
                  You have {status.backup_codes_remaining} codes remaining. Regenerate them to invalidate lost or exposed codes.
                </p>
                <Button variant="outline" onClick={regenerateCodes} data-testid="2fa-regenerate-btn">
                  Regenerate backup codes
                </Button>
              </CardContent>
            </Card>
            <Card className="border-red-200">
              <CardContent className="p-5 space-y-3">
                <h2 className="font-bold text-red-800">Disable 2FA</h2>
                <p className="text-sm text-slate-600">
                  Enter a live code (or a backup code) to turn off two-factor authentication.
                </p>
                <div className="flex gap-2">
                  <Input placeholder="6-digit code or backup code" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} className="max-w-[240px]" data-testid="2fa-disable-input" />
                  <Button variant="destructive" onClick={doDisable} disabled={busy || !disableCode.trim()} data-testid="2fa-disable-btn">
                    Disable
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default TwoFactorSettingsPage;
